"use client";

import React from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const services = [{
  title: "Classic Elegance",
  description: "Timeless imagery that captures the grace and tradition of your special day with a refined, soft-toned aesthetic and artistic composition.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1675594/img/classic_elegance.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1675594/img/classic_elegance.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1675594/img/classic_elegance.png-wvc-sizes",
  link: "/CollectionsPage"
}, {
  title: "Modern Romance",
  description: "Contemporary storytelling for the style-conscious couple, focusing on emotionally resonant moments and sophisticated visual narratives.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1675594/img/modern_romance.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1675594/img/modern_romance.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1675594/img/modern_romance.png-wvc-sizes",
  link: "/CollectionsPage"
}, {
  title: "Garden Ceremonies",
  description: "Natural lighting and organic compositions that highlight the ethereal beauty of outdoor celebrations and floral-rich environments.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1675594/img/garden_ceremonies.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1675594/img/garden_ceremonies.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1675594/img/garden_ceremonies.png-wvc-sizes",
  link: "/CollectionsPage"
}, {
  title: "Destination Weddings",
  description: "Breathtaking collections that blend the unique spirit of your chosen location with intimate, high-fashion wedding photography.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1675594/img/destination_weddings.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1675594/img/destination_weddings.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1675594/img/destination_weddings.png-wvc-sizes",
  link: "/CollectionsPage"
}, {
  title: "Intimate Elopements",
  description: "Personalized service for smaller gatherings, ensuring every quiet glance and shared secret is preserved in a beautifully curated album.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1675594/img/intimate_elopements.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1675594/img/intimate_elopements.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1675594/img/intimate_elopements.png-wvc-sizes",
  link: "/CollectionsPage"
}, {
  title: "Enchanted Moments",
  description: "A dreamy collection capturing intimate celebrations with soft lighting and timeless elegance.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1675594/img/enchanted_moments.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1675594/img/enchanted_moments.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1675594/img/enchanted_moments.png-wvc-sizes",
  link: "/CollectionsPage"
}];
export default function OurTermiteServices() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="259639" id="weddingcollectionsshowcase" className="bg-background text-foreground py-16 md:py-24 lg:py-32 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <div ref={headerRef} className={`flex flex-col items-center text-center max-w-3xl mx-auto mb-12 md:mb-16 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold text-foreground mb-4 tracking-tight">
            The Wedding Collections
          </h2>
          <p className="text-lg text-muted-foreground font-sans max-w-2xl">
            Explore our curated photography styles designed to reflect your unique love story through a lens of timeless sophistication and soft-toned artistry.
          </p>
        </div>

        <div ref={gridRef} className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 transition-all duration-1000 ease-out delay-200 ${isGridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {services.map((service, index) => {
          return <Card key={index} data-index={index} className="group flex flex-col h-full border-border/50 shadow-lg hover:shadow-xl transition-all duration-500 hover:-translate-y-1 bg-card">
                <CardHeader className="p-0 block">
                  <div className="w-full aspect-video mb-6 overflow-hidden rounded-t-xl">
                    <img src={service.image} data-wvc-srcset={service.srcset} data-wvc-sizes={service.sizes} alt={service.title} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity duration-300" loading="lazy" decoding="async" />
                  </div>
                  <CardTitle className="text-2xl font-default text-foreground px-6 mb-2">
                    {service.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex-grow pb-6">
                  <p className="text-muted-foreground font-sans leading-relaxed">
                    {service.description}
                  </p>
                </CardContent>
                <CardFooter className="pt-0 mt-auto">
                  <Button variant="ghost" className="p-0 h-auto hover:bg-transparent hover:text-primary text-primary/80 font-medium group/btn" asChild>
                    <Link to={service.link}>
                      View Collection
                      <ArrowRight className="w-4 h-4 ml-2 transition-transform duration-300 group-hover/btn:translate-x-1" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>;
        })}
        </div>

        
      </div>
    </section>;
}